import { useMemo } from "react";
import { Loader2, Calendar } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { usePredictedIncome } from "@/hooks/api";
import { Card, CardContent } from "@/components/ui/card";
import { ChartTableToggle } from "@/components/shared/chart-table-toggle";
import { useDashboardUrlState } from "@/components/dashboards/useDashboardUrlState";
import { ForecastBreakdown, Definition } from "./forecast-breakdown";
import { MEASURES, parseDimension, parseMeasure } from "./forecast-analysis";
import { forecastColor } from "./forecast-chart";
import { formatCurrency, formatMonth, toISODate } from "./shared/formatters";

export function Prognoser() {
  const predictedFrom = useMemo(() => toISODate(new Date()), []);
  const predictedTo = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return toISODate(d);
  }, []);

  const [state, update] = useDashboardUrlState({
    forecastProductMeasure: "income",
    forecastCustomerMeasure: "income",
    forecastDimension: "source",
    forecastProductView: "absolute",
    forecastCustomerView: "absolute",
    forecastProductMode: "chart",
    forecastCustomerMode: "chart",
  });
  const {
    data: predictedIncome,
    isLoading,
    isError,
  } = usePredictedIncome(predictedFrom, predictedTo);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
            <span className="text-sm text-admin-text-subtle">
              Henter prognoser fra Periode...
            </span>
          </div>
          <div className="h-[300px] animate-pulse bg-admin-card-border/50 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-admin-danger-500">
        Kunne ikke hente forventet inntekt.
      </p>
    );
  }

  if (!predictedIncome || predictedIncome.months.length === 0) {
    return (
      <p className="text-sm text-admin-text-subtle">
        Ingen prognosedata tilgjengelig.
      </p>
    );
  }

  const chartData = predictedIncome.months.map((m) => ({
    month: formatMonth(m.month),
    amount: m.available ? m.totalAmount / 100 : null,
    invoices: m.invoiceCount,
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-admin-primary" />
        <h3 className="text-lg font-semibold">
          Forventet inntekt{" "}
          <Definition
            text={`${MEASURES.income.definition} ${MEASURES.invoices.definition}`}
          />
        </h3>
        <span className="text-sm text-admin-text-subtle">
          {formatMonth(predictedIncome.months[0]!.month)} –{" "}
          {formatMonth(predictedIncome.months.at(-1)!.month)}{" "}
          <Definition text="Samme periode brukes i alle tre grafer. Periode hentes for hele kalendermåneder fra dagens måned til samme måned neste år (opptil 13 månedskolonner). Første og siste måned avkortes ikke til dagene i datointervallet. Prognosen inkluderer bare fakturaene som Periode allerede returnerer som PENDING; nye salg er ikke estimert." />
        </span>
      </div>
      {predictedIncome.months.some((month) => !month.available) && (
        <p role="alert" className="text-sm text-admin-warn-500">
          Ufullstendige data:{" "}
          {predictedIncome.months
            .filter((month) => !month.available)
            .map((month) => formatMonth(month.month))
            .join(", ")}{" "}
          kunne ikke hentes. Totaltall gjelder bare tilgjengelige måneder.
        </p>
      )}
      {predictedIncome.total.unmatchedInvoiceCount > 0 && (
        <p className="text-xs text-admin-text-subtle">
          {predictedIncome.total.unmatchedInvoiceCount} fakturaer er ikke koblet
          til en lokal kunde.{" "}
          <Definition text="Disse fakturaene er med i inntekt og fakturaantall under Ukjent, men ikke i unike kunder eller inntekt per kunde. Demografi og produkt er hentet fra lokale data; manglende synkronisering kan gi Ukjent." />
        </p>
      )}

      <ChartTableToggle
        chart={
          <Card>
            <CardContent className="p-4">
              <div className="mb-2 text-right text-xs text-admin-text-subtle">
                Totalt:{" "}
                {formatCurrency(predictedIncome.total.totalAmount / 100)} (
                {predictedIncome.total.invoiceCount} fakturaer)
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-admin-card-border)"
                  />
                  <XAxis
                    dataKey="month"
                    tick={{
                      fontSize: 11,
                      fill: "var(--color-admin-text-subtle)",
                    }}
                    axisLine={{ stroke: "var(--color-admin-card-border)" }}
                  />
                  <YAxis
                    tick={{
                      fontSize: 11,
                      fill: "var(--color-admin-text-subtle)",
                    }}
                    axisLine={{ stroke: "var(--color-admin-card-border)" }}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--color-admin-card)",
                      border: "1px solid var(--color-admin-card-border)",
                      borderRadius: 8,
                      color: "var(--color-admin-text-primary)",
                    }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
                          <p className="text-xs font-medium text-admin-text-primary mb-1">
                            {label}
                          </p>
                          <p className="text-xs text-admin-text-primary">
                            {formatCurrency(payload[0]!.value as number)}
                          </p>
                          <p className="text-xs text-admin-text-subtle">
                            {
                              (payload[0]!.payload as { invoices: number })
                                .invoices
                            }{" "}
                            fakturaer
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="amount"
                    fill={forecastColor(0)}
                    name="Forventet"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        }
        table={
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-admin-card-border">
                      <th className="text-left text-xs font-medium text-admin-text-subtle p-3">
                        Måned
                      </th>
                      <th className="text-right text-xs font-medium text-admin-text-subtle p-3">
                        Fakturaer{" "}
                        <Definition text={MEASURES.invoices.definition} />
                      </th>
                      <th className="text-right text-xs font-medium text-admin-text-subtle p-3">
                        Forventet beløp
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictedIncome.months.map((m) => (
                      <tr
                        key={m.month}
                        className="border-b border-admin-card-border last:border-0"
                      >
                        <td className="p-3 text-admin-text-primary">
                          {new Date(m.month + "-01").toLocaleDateString(
                            "nb-NO",
                            { month: "long", year: "numeric" },
                          )}
                        </td>
                        <td className="p-3 text-right text-admin-text-secondary">
                          {m.available ? m.invoiceCount : "Mangler data"}
                        </td>
                        <td className="p-3 text-right text-admin-text-secondary">
                          {m.available
                            ? formatCurrency(m.totalAmount / 100)
                            : "—"}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-admin-card-border">
                      <td className="p-3 font-bold text-admin-text-primary">
                        Totalt
                      </td>
                      <td className="p-3 text-right font-bold text-admin-text-primary">
                        {predictedIncome.total.invoiceCount}
                      </td>
                      <td className="p-3 text-right font-bold text-admin-text-primary">
                        {formatCurrency(
                          predictedIncome.total.totalAmount / 100,
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        }
      />
      <ForecastBreakdown
        forecast={predictedIncome}
        dimension="product"
        measure={parseMeasure(state.forecastProductMeasure)}
        view={state.forecastProductView}
        mode={state.forecastProductMode}
        onMeasure={(value) => update({ forecastProductMeasure: value })}
        onView={(value) => update({ forecastProductView: value })}
        onMode={(value) => update({ forecastProductMode: value })}
      />
      <ForecastBreakdown
        forecast={predictedIncome}
        dimension={parseDimension(state.forecastDimension)}
        measure={parseMeasure(state.forecastCustomerMeasure)}
        view={state.forecastCustomerView}
        mode={state.forecastCustomerMode}
        onMeasure={(value) => update({ forecastCustomerMeasure: value })}
        onView={(value) => update({ forecastCustomerView: value })}
        onMode={(value) => update({ forecastCustomerMode: value })}
        onDimension={(value) => update({ forecastDimension: value })}
      />
    </div>
  );
}
